import { gridSizeMdLg12 } from "@/shared";
import { useFetchUbicacionProductosEnabled, useGetContrato } from "@/shared/actions/erp";
import { useBuscarClientes } from "@/shared/actions/erp/tickets-soporte-cliente";
import { CustomAutocompleteNoForm, CustomTextArea } from "@/shared/components/ui";
import { CategoryProducCodeEnum } from "@/shared/interfaces/erp/inventario";
import { alpha, Box, Button, CircularProgress, FormControl, FormControlLabel, FormHelperText, Grid, Modal, Paper, Radio, RadioGroup, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import OtrosConceptosVisita from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/OtrosConceptosVisita";

const getCodeFromValue = (...values: any[]) => {
    for (const value of values) {
        if (value === undefined || value === null) continue;
        if (typeof value === "string") {
            if (value.trim() !== "") return value;
            continue;
        }
        if (typeof value === "object") {
            const code =
                value.codigo ||
                value.id_ubicacion ||
                value.id ||
                value.numero_referencia ||
                "";
            if (String(code).trim() !== "") return String(code);
        }
    }

    return "";
};

type OTDetalleVisitaProps = {
    form: any;
    errors: any;
    ordenTrabajo: any
    onSaveSolicitudCambioOnt: (data: any) => void;
    isSavingCambioOnt?: boolean;
}
export default function OTDetalleVisitaInstallTecnicoFormTab({
    form,
    errors,
    ordenTrabajo,
    onSaveSolicitudCambioOnt,
    isSavingCambioOnt
}: OTDetalleVisitaProps) {
    //state 
    const [isOpenModal, setIsModal] = useState(false)
    const [requiereCambioONT, setrequiereCambioONT] = useState<boolean | null>(
        ordenTrabajo?.requiere_cambio_ont ?? null
    )
    const locked = useMemo(
        () => ordenTrabajo?.requiere_cambio_ont !== undefined && ordenTrabajo?.requiere_cambio_ont !== null,
        [ordenTrabajo?.requiere_cambio_ont]
    )
    useEffect(() => {
        setrequiereCambioONT(ordenTrabajo?.requiere_cambio_ont ?? null);
    }, [ordenTrabajo?.requiere_cambio_ont]);
    const clienteIdentificacion = useMemo(() => {
        const ordenCliente = ordenTrabajo?.cliente_data || {};
        const contratoCliente = ordenTrabajo?.contrato_data?.cliente_data || {};
        const prospecto = ordenTrabajo?.prospecto_data || {};
        const preventa = ordenTrabajo?.preventa_data || {};
        return (
            ordenCliente.identificacion ||
            ordenCliente.cedula ||
            contratoCliente.identificacion ||
            contratoCliente.cedula ||
            prospecto.identificacion ||
            prospecto.cedula ||
            preventa.identificacion ||
            preventa.cedula ||
            ""
        );
    }, [ordenTrabajo]);
    const {
        data: clientesBusquedaRes,
        isLoading: isLoadingOntActual,
    } = useBuscarClientes({
        query: clienteIdentificacion,
        tipo: "identificacion",
        enabled: requiereCambioONT === true && !!clienteIdentificacion,
    });

    const contratoId = ordenTrabajo?.contrato_data?.id_contrato || ordenTrabajo?.contrato;
    const {
        data: contratoFallbackRes,
        isLoading: isLoadingContratoFallback,
    } = useGetContrato(contratoId, requiereCambioONT === true && !!contratoId);

    const clienteUbicacion = useMemo(() => {
        const contratoFallback = (contratoFallbackRes as any)?.data || {};
        const contratoFallbackCliente = contratoFallback?.cliente_data || {};
        const ordenCliente = ordenTrabajo?.cliente_data || {};
        const contratoCliente = ordenTrabajo?.contrato_data?.cliente_data || {};
        const contrato = ordenTrabajo?.contrato_data || {};
        return getCodeFromValue(
            contratoFallbackCliente.ubicacion,
            contratoFallbackCliente.ubicacion_id,
            contratoFallbackCliente.ubicacion_data,
            contratoFallback.ubicacion,
            contratoFallback.ubicacion_id,
            contratoFallback.ubicacion_data,
            ordenCliente.ubicacion,
            ordenCliente.ubicacion_id,
            ordenCliente.ubicacion_data,
            contratoCliente.ubicacion,
            contratoCliente.ubicacion_id,
            contratoCliente.ubicacion_data,
            contrato.ubicacion,
            contrato.ubicacion_id,
            contrato.ubicacion_data
        );
    }, [contratoFallbackRes, ordenTrabajo]);

    const {
        data: ubicacionProductosClienteRes,
        isLoading: isLoadingOntActualInventario,
    } = useFetchUbicacionProductosEnabled({
        enabled: requiereCambioONT === true && !!clienteUbicacion,
        params: {
            categoria_producto: CategoryProducCodeEnum.EQUIPOS,
            ubicacion: clienteUbicacion,
            es_ont: true,
        },
    });

    const ontActualInventarioOptions = useMemo(() => {
        const rows = ubicacionProductosClienteRes?.data?.data || [];
        return rows
            .filter((item: any) => item?.producto_data?.es_ont && Array.isArray(item?.series) && item.series.length > 0)
            .flatMap((item: any) => {
                return item.series.map((serie: string) => ({
                    name: `${item.producto_data?.descripcion || item.producto_data?.codigo || "ONT"} - ${serie} (Inventario ERP)`,
                    value: {
                        codigo: item.producto_data?.codigo || item.producto,
                        cantidad: 1,
                        series: [serie],
                        contrato: ordenTrabajo?.contrato_data?.numero_referencia || ordenTrabajo?.contrato_data?.codigo,
                        modelo: item.producto_data?.descripcion || item.producto_data?.modelo_producto,
                        marca: item.producto_data?.marca_producto,
                        producto_id: item.producto_data?.id_producto,
                        ubicacion: item.ubicacion,
                        bodega: item.bodega,
                        fuente: "inventario_erp",
                    },
                    contrato: ordenTrabajo?.contrato_data?.numero_referencia || ordenTrabajo?.contrato_data?.codigo,
                    serie,
                    modelo: item.producto_data?.descripcion || item.producto_data?.modelo_producto,
                    marca: item.producto_data?.marca_producto,
                    fuente: "inventario_erp",
                }));
            });
    }, [ordenTrabajo, ubicacionProductosClienteRes]);

    const ontActualOptions = useMemo(() => {
        const rows = clientesBusquedaRes?.data || [];
        const ticketsOptions = rows
            .filter((item: any) => item?.serie_ont)
            .map((item: any) => ({
                name: `${item.equipo_modelo || "ONT"} - ${item.serie_ont}${item.contrato_codigo ? ` (${item.contrato_codigo})` : ""}`,
                value: {
                    codigo: item.equipo_modelo || "ONT",
                    cantidad: 1,
                    series: [item.serie_ont],
                    contrato: item.contrato_codigo,
                    modelo: item.equipo_modelo,
                    marca: item.equipo_marca,
                },
                contrato: item.contrato_codigo,
                serie: item.serie_ont,
                modelo: item.equipo_modelo,
                marca: item.equipo_marca,
                fuente: "tickets",
            }));

        const inventarioSeries = new Set(ontActualInventarioOptions.map((item: any) => item.serie));
        const ticketsSinDuplicados = ticketsOptions.filter(
            (item: any) => !inventarioSeries.has(item.serie)
        );

        return [...ontActualInventarioOptions, ...ticketsSinDuplicados];
    }, [clientesBusquedaRes, ontActualInventarioOptions]);
    const ontActualGuardada =
        form.getValues()?.observaciones?.observacion_cambio_ont?.ont_actual;
    const hasOntActualGuardada = !!ontActualGuardada;
    const observacionesVisita = ordenTrabajo?.observaciones?.observacion_visita
    return (
        <>
            <Grid item xs={12} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary" gutterBottom>
                    ⚠️ Cambio de ONT
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    ¿El cliente requiere un cambio de equipo ONT en esta visita? *
                </Typography>
                <FormControl error={requiereCambioONT === null}>
                    <RadioGroup
                        row
                        value={requiereCambioONT === null ? '' : requiereCambioONT ? 'si' : 'no'}
                        onChange={(e) => {
                            const value = e.target.value === 'si';
                            setrequiereCambioONT(value);
                            form.setValue('requiere_cambio_ont', value);
                            if (value === false) {
                                setIsModal(true)
                            }

                        }}
                    >
                        <FormControlLabel
                            value="si"
                            control={<Radio color="primary" />}
                            label="Sí, requiere cambio"
                        />
                        <FormControlLabel
                            value="no"
                            control={<Radio color="primary" />}
                            label="No requiere cambio"
                        />
                    </RadioGroup>
                    {requiereCambioONT === null && (
                        <FormHelperText>
                            Debe seleccionar una opción (obligatorio) antes de finalizar la visita.
                        </FormHelperText>
                    )}
                </FormControl>
            </Grid>
            {(requiereCambioONT) && (
                <>
                    {!ordenTrabajo?.motivo_cambio_ont && (
                        <Grid item xs={12}>
                            <Paper
                                sx={{
                                    p: 1.5,
                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                    border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.3)}`,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Typography variant="body2" color="text.primary">
                                    ⚠️ Ingresa el motivo del cambio para que <strong>Activaciones</strong> pueda aprobarlo.
                                </Typography>
                            </Paper>
                        </Grid>

                    )}

                    <Grid item xs={12}>
                        <CustomTextArea
                            label="Motivo del cambio de ONT *"
                            name="motivo_cambio_ont"
                            defaultValue={form.getValues()?.motivo_cambio_ont || ""}
                            control={form.control}
                            error={errors?.motivo_cambio_ont}
                            helperText={errors?.motivo_cambio_ont?.message}
                            size={gridSizeMdLg12}
                            disabled={locked && !!ordenTrabajo?.motivo_cambio_ont}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                            Seleccionar ONT actual
                        </Typography>
                        {isLoadingOntActual || isLoadingContratoFallback || isLoadingOntActualInventario ? (
                            <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Buscando ONT actual del cliente...
                                </Typography>
                            </Box>
                        ) : ontActualOptions.length > 0 ? (
                            <CustomAutocompleteNoForm<any>
                                label="ONT actual instalada"
                                valueKey="name"
                                options={ontActualOptions}
                                defaultValue={ontActualGuardada?.name || ""}
                                onChangeRawValue={(value) => {
                                    form.setValue("observaciones.observacion_cambio_ont.ont_actual", value);
                                }}
                                size={gridSizeMdLg12}
                                disabled={locked && !!ordenTrabajo?.observaciones?.observacion_cambio_ont?.ont_actual}
                            />
                        ) : (
                            <Alert severity="warning">
                                No se encontró ONT actual en inventario ERP ni en tickets para la identificación {clienteIdentificacion || "-"}.
                                {clienteUbicacion ? ` Ubicación consultada: ${clienteUbicacion}.` : " El cliente no tiene ubicación asociada."}
                            </Alert>
                        )}
                    </Grid>
                   {/*  {(!ordenTrabajo?.motivo_cambio_ont || ordenTrabajo.requiere_cambio_ont === null) && ( */}
                        <Grid item xs={12} display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                color="primary"
                                disableElevation
                                disabled={isSavingCambioOnt || (locked && hasOntActualGuardada)}
                                onClick={() => {
                                    !ordenTrabajo?.fecha_hora_inicio_visita ?
                                    toast.error('Inicie la visita para continuar.')
                                    : onSaveSolicitudCambioOnt(form.getValues())
                                }}
                                startIcon={isSavingCambioOnt ? <CircularProgress size={16} color="inherit" /> : undefined}
                            >
                                {isSavingCambioOnt ? 'Enviando...' : 'Solicitar cambio de ONT'}
                            </Button>
                        </Grid>
                </>
            )}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mt: 4,
                    bgcolor: '#f0f7ff',
                    border: '1px solid #b3d4fc',
                    borderRadius: 2,
                }}
            >
                <Grid container spacing={1.5}>
                    <Grid item xs={12} display="flex" alignItems="center" gap={1}>
                        <InformationCircleIcon width={22} height={22} color="#1976d2" />
                        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                            Observación para el técnico
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Notas que el técnico debe tomar en cuenta durante la visita
                        </Typography>
                    </Grid>
                    {observacionesVisita ? (
                        <Grid item xs={12}>
                            <CustomTextArea
                                label=""
                                name=""
                                control={form.control}
                                defaultValue={observacionesVisita}
                                disabled={true}
                                rows={5}
                                typeOfEntry="observacion"
                                error={undefined}
                                helperText=""
                                size={gridSizeMdLg12}
                            />
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    bgcolor: '#fafafa',
                                    borderStyle: 'dashed',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    No hay observaciones registradas para el técnico.
                                </Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Paper>
            {/* "Otros conceptos" se movió al tab MATERIALES (el técnico asigna la
                serie de cada equipo ahí). */}

            <Modal
                open={isOpenModal}
                onClose={() => setIsModal(false)}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    minWidth: 400,
                }}>
                    <Typography variant="h6" gutterBottom>
                        Confirmar cambio de ONT
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        ¿Estás seguro que el cliente no requiere cambio de ONT?
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button variant="outlined" onClick={() => setIsModal(false)}>
                            Cancelar
                        </Button>
                        <Button variant="contained" color="primary" onClick={() => {
                            form.setValue('motivo_cambio_ont','')
                            form.setValue('observaciones.observacion_cambio_ont', null)
                            onSaveSolicitudCambioOnt(form.getValues())
                            setIsModal(false)
                        }}>
                            Sí, confirmar
                        </Button>
                    </Box>
                </Box>
            </Modal>

        </>

    )

}
